import type { KeywordRule, ResponseStrategy } from '../types';

interface MockResponseOptions {
  text: string;
  strategy: ResponseStrategy;
  responses: string[];
  keywordRules: KeywordRule[];
  sequenceIndex: number;
  fixedResponseIndex: number;
  delayMs: number;
  signal?: AbortSignal;
}

const chooseResponse = ({
  text,
  strategy,
  responses,
  keywordRules,
  sequenceIndex,
  fixedResponseIndex,
}: Omit<MockResponseOptions, 'delayMs' | 'signal'>) => {
  const safeResponses = responses.length > 0 ? responses : ['Спасибо за сообщение!'];

  if (strategy === 'keyword') {
    const normalizedText = text.toLocaleLowerCase('ru');
    const rule = keywordRules.find(({ keywords }) =>
      keywords.some((keyword) => normalizedText.includes(keyword.toLocaleLowerCase('ru'))),
    );
    return rule?.response || safeResponses[sequenceIndex % safeResponses.length];
  }

  if (strategy === 'random') {
    return safeResponses[Math.floor(Math.random() * safeResponses.length)];
  }

  if (strategy === 'fixed') {
    return safeResponses[Math.min(Math.max(fixedResponseIndex, 0), safeResponses.length - 1)];
  }

  return safeResponses[sequenceIndex % safeResponses.length];
};

export const mockResponseService = (options: MockResponseOptions): Promise<string> =>
  new Promise((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = window.setTimeout(
      () => {
        resolve(chooseResponse(options));
      },
      Math.max(0, options.delayMs),
    );

    options.signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
